import React, { useEffect, useState } from "react";
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent,
  TextField, IconButton, CircularProgress, Card, CardContent,
  Grid, Stack, Chip, Tooltip, Switch, FormControlLabel
} from "@mui/material";

import {
  Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon,
  Refresh as RefreshIcon, CalendarMonth as CalendarIcon,
  BeachAccess as HolidayIcon, Close as CloseIcon
} from "@mui/icons-material";

import Swal from "sweetalert2";
import { db } from "../../../firebase";
import {
  collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, where, orderBy, writeBatch
} from "firebase/firestore";

const THEME_TEAL = "#0D9488";

function Holidays() {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [isRange, setIsRange] = useState(false); // Toggle for multiple dates
  
  const schoolId = "zIlbJM5BjyrlEhg7fWKs";

  const [form, setForm] = useState({
    title: "",
    fromDate: new Date().toISOString().split("T")[0],
    toDate: new Date().toISOString().split("T")[0],
    description: "",
  });

  useEffect(() => { fetchHolidays(); }, []);

  const fetchHolidays = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "holidays"), where("schoolId", "==", schoolId), orderBy("date", "asc"));
      const snap = await getDocs(q);
      setHolidays(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  // Logic to generate all dates between From and To
  const getDatesInRange = (startDate, endDate) => {
    const dates = [];
    let curr = new Date(startDate);
    const last = new Date(endDate);
    while (curr <= last) {
      dates.push(new Date(curr).toISOString().split("T")[0]);
      curr.setDate(curr.getDate() + 1);
    }
    return dates;
  };

  const handleSubmit = async () => {
    if (!form.title || !form.fromDate) return Swal.fire("Error", "Title and Date required", "error");

    setLoading(true);
    try {
      if (editId) {
        // Single Update
        await updateDoc(doc(db, "holidays", editId), { ...form, date: form.fromDate, schoolId });
      } else if (isRange) {
        // Auto-generate multiple entries
        const dateList = getDatesInRange(form.fromDate, form.toDate);
        const batch = writeBatch(db);
        
        dateList.forEach((date) => {
          const docRef = doc(collection(db, "holidays"));
          batch.set(docRef, { title: form.title, date, description: form.description, schoolId });
        });
        await batch.commit();
      } else {
        // Single Add
        await addDoc(collection(db, "holidays"), { ...form, date: form.fromDate, schoolId });
      }
      
      Swal.fire("Success", "Holidays synchronized", "success");
      resetForm();
      fetchHolidays();
    } catch (err) { Swal.fire("Error", err.message, "error"); }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    const res = await Swal.fire({ title: "Delete?", icon: "warning", showCancelButton: true });
    if (res.isConfirmed) {
      await deleteDoc(doc(db, "holidays", id));
      fetchHolidays();
    }
  };

  const resetForm = () => {
    setForm({ title: "", fromDate: new Date().toISOString().split("T")[0], toDate: new Date().toISOString().split("T")[0], description: "" });
    setEditId(null);
    setOpen(false);
    setIsRange(false);
  };

  return (
    <Box p={2} sx={{ bgcolor: "#F8FAFC", minHeight: "100vh" }}>
      {/* Small Size Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight="900" color="#1E293B">Holidays</Typography>
        <Stack direction="row" spacing={1}>
          <IconButton size="small" onClick={fetchHolidays}><RefreshIcon fontSize="small" /></IconButton>
          <Button 
            size="small" variant="contained" startIcon={<AddIcon />} 
            onClick={() => setOpen(true)} sx={{ bgcolor: THEME_TEAL, textTransform: 'none' }}
          >
            Add
          </Button>
        </Stack>
      </Stack>

      <Grid className="row" spacing={1.5}>
        {holidays.map((item) => (
          <Grid className="col-sm-3 mb-3" item xs={12} sm={6} md={4} lg={3} key={item.id}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: "12px !important" }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ bgcolor: "#F0FDFA", p: 1, borderRadius: 1.5, textAlign: 'center', minWidth: 45 }}>
                    <Typography variant="caption" fontWeight="bold" color={THEME_TEAL} display="block">
                      {new Date(item.date).toLocaleString('default', { month: 'short' }).toUpperCase()}
                    </Typography>
                    <Typography variant="h6" fontWeight="900" lineHeight={1}>{new Date(item.date).getDate()}</Typography>
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" fontWeight="bold" noWrap>{item.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{new Date(item.date).toLocaleDateString()}</Typography>
                  </Box>
                  <Stack>
                    <IconButton size="small" onClick={() => { setEditId(item.id); setForm({...item, fromDate: item.date}); setOpen(true); }}><EditIcon fontSize="inherit" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(item.id)}><DeleteIcon fontSize="inherit" /></IconButton>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Optimized Small Dialog */}
      <Dialog open={open} onClose={resetForm} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: "bold", fontSize: '1rem' }}>
          {editId ? "Edit Entry" : "Add Holiday"}
          {!editId && (
            <FormControlLabel
              control={<Switch size="small" checked={isRange} onChange={(e) => setIsRange(e.target.checked)} />}
              label={<Typography variant="caption">Multiple Days</Typography>}
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={2}>
            <TextField label="Title" name="title" size="small" fullWidth value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} />
            
            <Stack direction="row" spacing={1}>
              <TextField label={isRange ? "From" : "Date"} type="date" name="fromDate" size="small" fullWidth InputLabelProps={{ shrink: true }} value={form.fromDate} onChange={(e) => setForm({...form, fromDate: e.target.value})} />
              {isRange && (
                <TextField label="To" type="date" name="toDate" size="small" fullWidth InputLabelProps={{ shrink: true }} value={form.toDate} onChange={(e) => setForm({...form, toDate: e.target.value})} />
              )}
            </Stack>

            <TextField label="Description" multiline rows={2} size="small" fullWidth value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} />
            
            <Button fullWidth variant="contained" onClick={handleSubmit} sx={{ bgcolor: THEME_TEAL }} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : "Save Entries"}
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default Holidays;